/**
 * Money is stored as integer minor units (paise/cents) + an ISO currency code.
 * Never use floating point for money (spec section 8).
 */
export interface Money {
  amount: number; // integer minor units
  currency: string; // ISO 4217, e.g. "INR"
}

export const DEFAULT_CURRENCY = 'INR';

const MINOR_UNITS: Record<string, number> = {
  INR: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  JPY: 0,
};

export function minorUnitDigits(currency: string): number {
  return MINOR_UNITS[currency.toUpperCase()] ?? 2;
}

export function money(amount: number, currency = DEFAULT_CURRENCY): Money {
  if (!Number.isInteger(amount)) {
    throw new Error(`Money.amount must be an integer minor unit value, got ${amount}`);
  }
  return { amount, currency: currency.toUpperCase() };
}

/** Convert a major-unit decimal (e.g. 199.99) to minor units (19999). */
export function toMinorUnits(major: number, currency = DEFAULT_CURRENCY): number {
  const factor = 10 ** minorUnitDigits(currency);
  return Math.round(major * factor);
}

/** Convert minor units to a major-unit number for display formatting only. */
export function toMajorUnits(minor: number, currency = DEFAULT_CURRENCY): number {
  const factor = 10 ** minorUnitDigits(currency);
  return minor / factor;
}

export function formatMoney(
  value: Money | number,
  currency = DEFAULT_CURRENCY,
  locale = 'en-IN',
): string {
  const m: Money = typeof value === 'number' ? { amount: value, currency } : value;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: m.currency,
    minimumFractionDigits: minorUnitDigits(m.currency),
  }).format(toMajorUnits(m.amount, m.currency));
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(`Currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

/** Percentage discount applied to an integer minor-unit amount. */
export function applyPercentDiscount(amount: number, percent: number): number {
  return Math.round(amount * (1 - percent / 100));
}
